import { Excluidos, iCargosSuperiores, iCriteriosPontuacao, iSolicitacaoReajuste, iTrilhaDeCarreira } from '../../../interfaces'

export interface IStatusAjustes {
  missao: IAlteracoes
  cargosSuperiores: IAlteracoes
  cargosSubordinados: IAlteracoes
  atribuicoes: IAlteracoes
  requisitosTecnicos: IAlteracoes
  criterios: IAlteracoes
  requisitosComportamentais: IAlteracoes
}

export interface IAlteracoes {
  temAlteracao: boolean
  quantidadeAlteracoes: number
  alteracoes?: string[]
  alteracoesInclusao?: string[]
  alteracoesDicionario?: Record<string, string[]>
  alteracoesInclusaoDicionario?: Record<string, string[]>
  alteracao?: string
  solicitacoesId?: string[]
}

// Aceita tanto o cargo atual quanto um snapshot congelado como base da comparacao
// Formato minimo que serve tanto para o snapshot congelado quanto para o cargo atual
export interface iNivelComparavel {
  atribuicoes?: string[]
  conhecimentosTecnicos?: string[]
  criteriosPontuacao?: iCriteriosPontuacao[]
}

export interface iOrigemAjustes {
  missao?: string
  competenciasComportamentais?: string[]
  cargosSuperiores?: iCargosSuperiores[]
  trilhasDeCarreira?: iTrilhaDeCarreira[]
  niveis?: Record<string, iNivelComparavel>
}

const GUID_VAZIO = '00000000-0000-0000-0000-000000000000'

export type OperacaoItem = 'inclusao' | 'remocao' | 'alteracao'

export interface iItemSolicitado {
  categoria: string
  nivel?: string
  operacao: OperacaoItem
  posicao?: number
  valorAnterior?: string
  valorSolicitado?: string
  motivo?: string
  // Caminho do campo na solicitacao: e por ele que a exclusao original e reencontrada na hora de aplicar
  caminho?: string
  // Cargos e trilhas sao exibidos pelo nome, mas so o id remonta a lista na hora de aplicar
  idAnterior?: string
  idSolicitado?: string
  trilhaAnterior?: iTrilhaDeCarreira
  trilhaSolicitada?: iTrilhaDeCarreira
}

export function listasStringSaoDiferentes (
  listaOriginal?: string[],
  listaAjuste?: string[]
): boolean {
  const original = (listaOriginal ?? []).filter(Boolean)
  const ajuste = (listaAjuste ?? []).filter(Boolean)

  if (original.length !== ajuste.length) {
    return true
  }

  return original.some(item => !ajuste.includes(item))
}

export function criteriosSaoDiferentes (
  criteriosOriginais?: iCriteriosPontuacao[],
  criteriosReajuste?: iCriteriosPontuacao[]
): boolean {
  const original = criteriosOriginais ?? []
  const reajuste = criteriosReajuste ?? []

  if (original.length !== reajuste.length) {
    return true
  }

  return original.some((criterioOriginal) => {
    return !reajuste.some((criterioReajuste) =>
      criterioReajuste.criterioId === criterioOriginal.criterioId &&
      criterioReajuste.opcaoPontuacaoId === criterioOriginal.opcaoPontuacaoId &&
      criterioReajuste.minimo === criterioOriginal.minimo
    )
  })
}

// campoExcluido e um caminho, nao o texto do item: niveis[Pleno].atribuicoes[3]
export const rotuloTipoItem: Record<number, string> = {
  0: 'Cargos superiores',
  1: 'Trilhas de carreira',
  2: 'Missão',
  3: 'Atribuições',
  4: 'Conhecimentos técnicos',
  5: 'Requisitos comportamentais'
}

export interface iCampoExcluidoDescrito {
  categoria: string
  nivel?: string
  posicao?: number
}

export function descreverCampoExcluido (exclusao: Excluidos): iCampoExcluidoDescrito {
  const caminho = exclusao.campoExcluido ?? ''
  const indice = /\[(\d+)\]$/.exec(caminho)?.[1]

  return {
    categoria: rotuloTipoItem[exclusao.tipoItem] ?? 'Item',
    nivel: /niveis\[([^\]]+)\]/.exec(caminho)?.[1]?.replaceAll('_', ' ').trim(),
    posicao: indice !== undefined ? Number(indice) + 1 : undefined
  }
}

function diffLista (
  categoria: string,
  nivel: string | undefined,
  anterior: string[] | undefined,
  solicitado: string[] | undefined,
  caminhoDe: (indice: number) => string,
  motivoDoCaminho: (caminho: string) => string | undefined
): iItemSolicitado[] {
  const antes = anterior ?? []
  const depoisBruto = solicitado ?? []
  const depois = depoisBruto.filter(Boolean)

  const itens: iItemSolicitado[] = []
  const consumidosNoDepois = new Set<string>()

  // Texto trocado na mesma posicao e edicao do ponto, nao remocao seguida de inclusao
  antes.forEach((valor, indice) => {
    if (!valor) return

    const naMesmaPosicao = depoisBruto[indice]
    const motivo = motivoDoCaminho(caminhoDe(indice))

    // Excluir o ponto e incluir outro produz a mesma lista que editar o ponto.
    // O motivo de exclusao registrado pelo assinante e o que desempata.
    if (
      motivo === undefined &&
      naMesmaPosicao &&
      naMesmaPosicao !== valor &&
      !depois.includes(valor) &&
      !antes.includes(naMesmaPosicao)
    ) {
      consumidosNoDepois.add(naMesmaPosicao)
      itens.push({
        categoria,
        nivel,
        operacao: 'alteracao',
        posicao: indice + 1,
        valorAnterior: valor,
        valorSolicitado: naMesmaPosicao,
        motivo,
        caminho: caminhoDe(indice)
      })
      return
    }

    if (!depois.includes(valor)) {
      itens.push({
        categoria,
        nivel,
        operacao: 'remocao',
        posicao: indice + 1,
        valorAnterior: valor,
        motivo,
        caminho: caminhoDe(indice)
      })
    }
  })

  depois
    .filter(item => !antes.includes(item) && !consumidosNoDepois.has(item))
    .forEach(valor => itens.push({ categoria, nivel, operacao: 'inclusao', valorSolicitado: valor }))

  return itens
}

// Compara os dois snapshots congelados no pedido, entao continua correto depois do ajuste ser aplicado
// Sem snapshot, o proprio DescritivoReajuste ainda carrega o estado pedido enquanto
// a solicitacao nao for aplicada, e o cargo atual serve de "antes"
export function listarItensSolicitados (
  solicitacao: iSolicitacaoReajuste,
  cargoAtual?: iOrigemAjustes
): iItemSolicitado[] {
  const anterior = solicitacao.estadoAnterior ?? cargoAtual
  const solicitado = solicitacao.estadoSolicitado ?? solicitacao

  if (!anterior || !solicitado) return []

  // O nome do nivel dentro do caminho pode vir com espaco ou underscore dependendo de quando
  // foi gravado (mesma inconsistencia de niveisDe acima), entao a comparacao nao pode ser exata
  const normalizarCaminho = (caminho: string): string =>
    caminho.replace(/^niveis\[([^\]]+)\]/, (_match, nivel: string) => `niveis[${nivel.replaceAll('_', ' ').trim()}]`)

  const motivoDoCaminho = (caminho: string): string | undefined =>
    solicitacao.excluidosMotivos?.find(e => normalizarCaminho(e.campoExcluido ?? '') === normalizarCaminho(caminho))?.motivoExclusao

  const itens: iItemSolicitado[] = []

  // A tela do assinante envia os niveis com espaco trocado por underscore e o back nao desfaz,
  // entao os dois lados do snapshot precisam ser casados pela chave normalizada
  const niveisDe = (niveis?: Record<string, iNivelComparavel>): Record<string, iNivelComparavel> =>
    Object.fromEntries(Object.entries(niveis ?? {}).map(([chave, nivel]) => [chave.replaceAll('_', ' '), nivel]))

  const niveisAnterior = niveisDe(anterior.niveis)
  const niveisSolicitado = niveisDe(solicitado.niveis)

  if ((anterior.missao ?? '') !== (solicitado.missao ?? '')) {
    itens.push({
      categoria: 'Missão',
      operacao: 'alteracao',
      valorAnterior: anterior.missao,
      valorSolicitado: solicitado.missao,
      motivo: motivoDoCaminho('missao'),
      caminho: 'missao'
    })
  }

  itens.push(...diffLista(
    'Requisitos comportamentais',
    undefined,
    anterior.competenciasComportamentais,
    solicitado.competenciasComportamentais,
    i => `competenciasComportamentais[${i}]`,
    motivoDoCaminho
  ))

  const nomesNiveis = Array.from(new Set([
    ...Object.keys(niveisAnterior),
    ...Object.keys(niveisSolicitado)
  ]))

  nomesNiveis.forEach((nomeNivel) => {
    itens.push(...diffLista(
      'Atribuições',
      nomeNivel,
      niveisAnterior[nomeNivel]?.atribuicoes,
      niveisSolicitado[nomeNivel]?.atribuicoes,
      i => `niveis[${nomeNivel}].atribuicoes[${i}]`,
      motivoDoCaminho
    ))

    itens.push(...diffLista(
      'Conhecimentos técnicos',
      nomeNivel,
      niveisAnterior[nomeNivel]?.conhecimentosTecnicos,
      niveisSolicitado[nomeNivel]?.conhecimentosTecnicos,
      i => `niveis[${nomeNivel}].conhecimentosTecnicos[${i}]`,
      motivoDoCaminho
    ))

    if (criteriosSaoDiferentes(
      niveisAnterior[nomeNivel]?.criteriosPontuacao,
      niveisSolicitado[nomeNivel]?.criteriosPontuacao
    )) {
      itens.push({ categoria: 'Critérios de pontuação', nivel: nomeNivel, operacao: 'alteracao' })
    }
  })

  const idNormalizado = (valor?: string): string => {
    const limpo = (valor ?? '').trim()

    return limpo === GUID_VAZIO ? '' : limpo
  }

  const rotuloCargoSuperior = (cargo?: iCargosSuperiores): string =>
    (cargo?.nome ?? '') || (idNormalizado(cargo?.cargoSuperiorId) === '' ? 'Ninguém (Topo da hierarquia)' : cargo?.cargoSuperiorId ?? '')

  const cargosSuperioresAntes = anterior.cargosSuperiores ?? []
  const cargosSuperioresDepois = solicitado.cargosSuperiores ?? []

  const porIdCargoSuperior = new Map<string, iCargosSuperiores>()
  cargosSuperioresAntes.forEach(c => porIdCargoSuperior.set(idNormalizado(c.cargoSuperiorId), c))
  cargosSuperioresDepois.forEach(c => porIdCargoSuperior.set(idNormalizado(c.cargoSuperiorId), c))

  diffLista(
    'Cargos superiores',
    undefined,
    cargosSuperioresAntes.map(c => idNormalizado(c.cargoSuperiorId)),
    cargosSuperioresDepois.map(c => idNormalizado(c.cargoSuperiorId)),
    i => `cargosSuperiores[${i}]`,
    motivoDoCaminho
  ).forEach((item) => {
    const cargoSuperiorAnterior = item.valorAnterior !== undefined ? porIdCargoSuperior.get(item.valorAnterior) : undefined
    const cargoSuperiorSolicitado = item.valorSolicitado !== undefined ? porIdCargoSuperior.get(item.valorSolicitado) : undefined

    itens.push({
      ...item,
      valorAnterior: cargoSuperiorAnterior ? rotuloCargoSuperior(cargoSuperiorAnterior) : item.valorAnterior,
      idAnterior: cargoSuperiorAnterior?.cargoSuperiorId,
      valorSolicitado: cargoSuperiorSolicitado ? rotuloCargoSuperior(cargoSuperiorSolicitado) : item.valorSolicitado,
      idSolicitado: cargoSuperiorSolicitado?.cargoSuperiorId
    })
  })

  const trilhasAntes = anterior.trilhasDeCarreira ?? []
  const trilhasDepois = solicitado.trilhasDeCarreira ?? []

  // Uma area pode ter varios saltos no mesmo cargo (ex: estagiario -> jr -> pleno), entao a
  // area sozinha nao identifica a trilha. Usa o mesmo algoritmo posicional das listas de texto
  // (diffLista), sobre uma chave area+cargo: editar uma linha no lugar vira alteracao, e so
  // cai pra exclusao+inclusao quando a linha some/aparece de verdade
  const chaveDaTrilha = (t: iTrilhaDeCarreira): string => `${idNormalizado(t.areaId)}|${idNormalizado(t.cargoSuperiorId)}`

  const porChave = new Map<string, iTrilhaDeCarreira>()
  trilhasAntes.forEach(t => porChave.set(chaveDaTrilha(t), t))
  trilhasDepois.forEach(t => porChave.set(chaveDaTrilha(t), t))

  // O nome do cargo e o dado legivel; area e so complemento. Sem os dois, sobra so o guid interno
  const rotuloTrilha = (trilha: iTrilhaDeCarreira): string => {
    const cargo = (trilha.cargoSuperiorNome ?? '') || (idNormalizado(trilha.cargoSuperiorId) === '' ? 'Ninguém (Topo da hierarquia)' : trilha.cargoSuperiorId ?? '')

    return trilha.areaNome ? `${cargo} (${trilha.areaNome})` : cargo
  }

  diffLista(
    'Trilhas de carreira',
    undefined,
    trilhasAntes.map(chaveDaTrilha),
    trilhasDepois.map(chaveDaTrilha),
    i => `trilhasDeCarreira[${i}]`,
    motivoDoCaminho
  ).forEach((item) => {
    const trilhaAnterior = item.valorAnterior !== undefined ? porChave.get(item.valorAnterior) : undefined
    const trilhaSolicitada = item.valorSolicitado !== undefined ? porChave.get(item.valorSolicitado) : undefined

    itens.push({
      ...item,
      valorAnterior: trilhaAnterior ? rotuloTrilha(trilhaAnterior) : item.valorAnterior,
      valorSolicitado: trilhaSolicitada ? rotuloTrilha(trilhaSolicitada) : item.valorSolicitado,
      trilhaAnterior,
      trilhaSolicitada
    })
  })

  // Item sem rotulo nao diz nada a quem le, e so aparece quando os dois lados vieram vazios
  return itens.filter(item =>
    (item.valorAnterior ?? '').trim() !== '' ||
    (item.valorSolicitado ?? '').trim() !== '' ||
    item.operacao === 'alteracao'
  )
}

export function contarItensSolicitados (solicitacao: iSolicitacaoReajuste, cargoAtual?: iOrigemAjustes): number {
  const itens = listarItensSolicitados(solicitacao, cargoAtual)

  if (itens.length > 0) return itens.length

  // Solicitacao anterior aos snapshots: sobra o que nunca foi sobrescrito
  return solicitacao.excluidosMotivos?.length ?? 0
}
